/**
 * clientSlice — Redux state for clients (buyers, sellers, both).
 *
 * Thunks:
 *  fetchClients       — paginated list; merges persisted filters with override params
 *  fetchClientById    — single client with dealer stats
 *  createClient       — insert and prepend to local list
 *  updateClient       — patch and update entry in local list
 *  deleteClient       — remove from local list
 *  exportClientsToCSV — stream CSV blob and trigger browser download
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/axios';

const initialState = {
  clients: [],
  currentClient: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  },
  filters: {
    search: '',
    type: '',
    city: '',
    dateFrom: '',
    dateTo: '',
    showDealersOnly: false,
    sortBy: 'created_at',
    sortOrder: 'DESC'
  }
};

export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { filters, pagination } = getState().clients;

      const queryParams = {
        search: params.search !== undefined ? params.search : filters.search,
        type: params.type !== undefined ? params.type : filters.type,
        city: params.city !== undefined ? params.city : filters.city,
        dateFrom: params.dateFrom !== undefined ? params.dateFrom : filters.dateFrom,
        dateTo: params.dateTo !== undefined ? params.dateTo : filters.dateTo,
        page: params.page || pagination.page,
        limit: params.limit || pagination.limit,
        sortBy: params.sortBy || filters.sortBy,
        sortOrder: params.sortOrder || filters.sortOrder
      };

      const showDealersValue = params.showDealersOnly !== undefined ? params.showDealersOnly : filters.showDealersOnly;
      if (showDealersValue) {
        queryParams.showDealersOnly = 'true';
      }

      const response = await api.get('/api/clients', { params: queryParams });
      return {
        clients: response.data.clients,
        pagination: response.data.pagination,
        filters: queryParams
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка отримання клієнтів');
    }
  }
);

export const fetchClientById = createAsyncThunk(
  'clients/fetchClientById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/clients/${id}`);
      return response.data.client;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка отримання клієнта');
    }
  }
);

export const createClient = createAsyncThunk(
  'clients/createClient',
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/clients', clientData);
      return response.data.client;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка створення клієнта');
    }
  }
);

export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async ({ id, clientData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/api/clients/${id}`, clientData);
      return response.data.client;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка оновлення клієнта');
    }
  }
);

export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/api/clients/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка видалення клієнта');
    }
  }
);

export const exportClientsToCSV = createAsyncThunk(
  'clients/exportToCSV',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { filters } = getState().clients;

      const response = await api.get('/api/clients/export/csv', {
        params: {
          type: filters.type,
          city: filters.city,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка експорту клієнтів');
    }
  }
);

const clientSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    setCurrentClient: (state, action) => { state.currentClient = action.payload; },
    clearCurrentClient: (state) => { state.currentClient = null; },
    setFilters: (state, action) => { state.filters = { ...state.filters, ...action.payload }; },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        type: '',
        city: '',
        dateFrom: '',
        dateTo: '',
        showDealersOnly: false,
        sortBy: 'created_at',
        sortOrder: 'DESC'
      };
    },
    setPage: (state, action) => { state.pagination.page = action.payload; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload.clients;
        state.pagination = action.payload.pagination;
        state.filters = { ...state.filters, ...action.payload.filters };
      })
      .addCase(fetchClients.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(fetchClientById.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchClientById.fulfilled, (state, action) => { state.loading = false; state.currentClient = action.payload; })
      .addCase(fetchClientById.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(createClient.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createClient.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(updateClient.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.clients.findIndex(c => c.id === action.payload.id);
        if (index !== -1) state.clients[index] = action.payload;
      })
      .addCase(updateClient.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(deleteClient.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = state.clients.filter(c => c.id !== action.payload);
        state.pagination.total -= 1;
      })
      .addCase(deleteClient.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(exportClientsToCSV.pending, (state) => { state.loading = true; })
      .addCase(exportClientsToCSV.fulfilled, (state) => { state.loading = false; })
      .addCase(exportClientsToCSV.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const {
  clearError,
  setCurrentClient,
  clearCurrentClient,
  setFilters,
  clearFilters,
  setPage
} = clientSlice.actions;

export default clientSlice.reducer;
